import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { useToast } from "vue-toastification";
import { useUserStore } from "@/stores/user.js";
import { event } from "vue-gtag";

import news from "@/assets/news.json";

export const useAppStore = defineStore("app", () => {
  const userStore = useUserStore();

  const toast = useToast();

  const currentRoute = ref(null);

  // Onboarding
  const isOnboarded = ref(false);
  const onboardingIsLive = ref(false);
  const onboardingPage = ref('');
  const chapterState = ref(null);
  const skipStep = ref(false);
  const forceExit = ref(false);

  // Sidebar
  const sidebarIsMounted = ref(false);
  const sidebarIsOpen = ref(false);
  const sidebarFooterIsOpen = ref(false);

  // Campaign
  const currentCampaignTab = ref("Finished");
  const activatedTableHasNews = ref({});
  const candidatePanelIsOpen = ref(false);
  const ponderationIsVisible = ref(false);
  const showCampaignLinkInfo = ref(false);
  const showCampaignParameters = ref(false);
  const campaignParametersTab = ref(0);

  // Payment
  const paymentModalIsOpen = ref(false);
  const creditsNeeded = ref(1);

  // News
  const viewedNewsIds = ref([]);
  const skipNewsForSession = ref(false);
  
  // Load viewed news IDs from localStorage
  if (localStorage.getItem('viewedNews')) {
    try {
      viewedNewsIds.value = JSON.parse(localStorage.getItem('viewedNews'));
    } catch (e) {
      viewedNewsIds.value = [];
    }
  }

  const currentNews = computed(() => {
    if (!news || news.length === 0 || skipNewsForSession.value) return null;
    
    // Find the first news that hasn't been viewed and is within date range
    const now = new Date();
    for (let i = news.length - 1; i >= 0; i--) {
      const newsItem = news[i];
      
      // Check if news has date limits
      if (newsItem.validUntil && new Date(newsItem.validUntil) < now) {
        continue; // Skip this news if it's expired
      }
      
      if (newsItem.validFrom && new Date(newsItem.validFrom) > now) {
        continue; // Skip this news if it's not yet valid
      }
      
      // Return this news if it hasn't been viewed
      if (!viewedNewsIds.value.includes(newsItem.id)) {
        return newsItem;
      }
    }
    
    return null; // No valid news to show
  });

  if (localStorage.getItem("isOnboarded")) {
    isOnboarded.value = JSON.parse(localStorage.getItem("isOnboarded"));
  }

  watch(
    isOnboarded,
    (value) => {
      localStorage.setItem("isOnboarded", value);
    },
    { deep: true }
  );

  // News funcs
  function dismissNews(dontShowAgain) {
    if (currentNews.value) {
      if (dontShowAgain) {
        // Add current news ID to viewed list
        viewedNewsIds.value.push(currentNews.value.id);
        
        // Save to localStorage
        localStorage.setItem('viewedNews', JSON.stringify(viewedNewsIds.value));
      }
      
      // No matter what, prevent showing more news until next refresh
      skipNewsForSession.value = true;
    }
  }

  // Sidebar funcs
  function toggleSidebar(value) {
    sidebarIsOpen.value = value;

    toggleSidebarFooter(false);
  }

  function toggleSidebarFooter(value) {
    sidebarFooterIsOpen.value = value;
  }

  // Campaign funcs
  function changeTab(tab) {
    currentCampaignTab.value = tab;
  }

  // Campaign funcs for onboarding
  function goAddCandidatesOnBoard() {
    if(onboardingPage.value != "addCandidates") {
      this.$router.push({ name: "addCandidates",
                          params: {
                            campaignId: '8d9740ed-56ac-4f1f-856f-df9d81f16c52',
                          } });
      onboardingPage.value = "addCandidates";
    }
  }

  // Go back to campaign funcs for onboarding
  function goBackCampaignOnBoard() {
    if(onboardingPage.value != "campaign") {
      this.$router.push({ name: "campaign-demonstration" });
      onboardingPage.value = "campaign";
    }
  }

  // Payment funcs
  function openPaymentModal(source) {
    if (userStore.hasPermission("payments", 2)) {
      paymentModalIsOpen.value = true;
      event("begin_checkout", { event_label: source });
    } else {
      toast.info(
        "Veuillez vous rapprocher d'un administrateur pour recharger votre solde de crédits."
      );
    }
  }

  // Onboarding funcs
  function restartOnboarding() {
    isOnboarded.value = false;
    onboardingIsLive.value = false;
    this.$router.push({ name: "root" });
    onboardingPage.value = "home";
  }

  function exitOnboarding() {
    forceExit.value = true;
    this.$introJs.exit(true);
  }

  function startChapter(chapterData) {
    if (isOnboarded.value) return;

    chapterState.value = chapterData;

    const { steps, afterComplete, nextRoute } = chapterState.value;

    onboardingIsLive.value = true;
    chapterState.value.isComplete = false;

    this.$introJs
      .onbeforechange(async () => {
        const currStep = this.$introJs.currentStep() + 1;

        if (currStep > 1 && currStep <= steps.length && !skipStep.value) {
          if (steps[currStep - 1].before) {
            steps[currStep - 1].before();

            setTimeout(() => {
              skipStep.value = true;
              this.$introJs.refresh({ refreshSteps: true });
              this.$introJs.goToStep(currStep);
            }, steps[currStep - 1].waitChange);

            return false;
          }
        } else {
          skipStep.value = false;
        }
      })
      .onbeforeexit(() => {
        if (!chapterState.value.isComplete && !forceExit.value) {
          return window.confirm(
            "Souhaitez-vous vraiment quitter l'onboarding ?"
          );
        }
      })
      .onexit(() => {
        if (!chapterState.value.isComplete) {
          toast.warning(
            'Vous souhaitez réaliser votre onboarding ultérieurement ? Vous pouvez recommencer le tutoriel à tout moment depuis la page "Mon profil" (en bas à gauche de votre écran).'
          );
          isOnboarded.value = true;
          onboardingIsLive.value = false;
          this.$router.push({ name: "home" });
        }

        forceExit.value = false;
      })
      .oncomplete((step, mode) => {
        if (mode !== "skip") {
          chapterState.value.isComplete = true;
          if (afterComplete) afterComplete();

          if (nextRoute) this.$router.push(nextRoute);
        }
      })
      .setOptions(chapterState.value)
      .start();
  }

  return {
    activatedTableHasNews,
    candidatePanelIsOpen,
    chapterState,
    creditsNeeded,
    currentCampaignTab,
    currentRoute,
    isOnboarded,
    onboardingIsLive,
    paymentModalIsOpen,
    ponderationIsVisible,
    showCampaignLinkInfo,
    showCampaignParameters,
    campaignParametersTab,
    sidebarFooterIsOpen,
    sidebarIsMounted,
    sidebarIsOpen,
    exitOnboarding,
    openPaymentModal,
    toggleSidebar,
    toggleSidebarFooter,
    changeTab,
    goAddCandidatesOnBoard,
    goBackCampaignOnBoard,
    restartOnboarding,
    startChapter,
    currentNews,
    dismissNews,
  };
});
